import clsx from 'clsx';
import { UserStatus } from '@/types';

interface AvatarProps {
  src?: string | null;
  username: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  status?: UserStatus;
  className?: string;
}

const sizes = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-12 h-12 text-lg',
};

const statusColors: Record<UserStatus, string> = {
  ONLINE: 'bg-green-500',
  AWAY: 'bg-yellow-500',
  DND: 'bg-red-500',
  OFFLINE: 'bg-surface-600',
};

const statusSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
};

function initials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

function colorFromUsername(username: string): string {
  const colors = [
    'bg-red-700', 'bg-orange-700', 'bg-amber-700', 'bg-yellow-700',
    'bg-green-700', 'bg-teal-700', 'bg-cyan-700', 'bg-blue-700',
    'bg-indigo-700', 'bg-violet-700', 'bg-purple-700', 'bg-pink-700',
  ];
  let hash = 0;
  for (const c of username) hash = (hash << 5) - hash + c.charCodeAt(0);
  return colors[Math.abs(hash) % colors.length];
}

export default function Avatar({ src, username, size = 'md', status, className }: AvatarProps) {
  return (
    <div className={clsx('relative flex-shrink-0', className)}>
      {src ? (
        <img
          src={src}
          alt={username}
          className={clsx('rounded-full object-cover', sizes[size])}
        />
      ) : (
        <div
          className={clsx(
            'rounded-full flex items-center justify-center font-semibold text-white select-none',
            sizes[size],
            colorFromUsername(username),
          )}
        >
          {initials(username)}
        </div>
      )}
      {status && (
        <span
          className={clsx(
            'absolute bottom-0 right-0 rounded-full border-2 border-surface-900',
            statusSizes[size],
            statusColors[status],
          )}
        />
      )}
    </div>
  );
}
